import { useState } from 'react';
import { Alert, Anchor, Code, Collapse, Group, Stack, Text } from '@mantine/core';
import { AlertTriangle, CheckCircle2, FileText, XCircle } from 'lucide-react';
import type { ConnectionTestCheck } from '@bmai/shared';
import type { IntegrationValidationState } from './IntegrationsPageViewModel';

interface IntegrationValidationDetailsProps {
    validation: IntegrationValidationState;
}

export default function IntegrationValidationDetails({ validation }: IntegrationValidationDetailsProps): React.ReactElement | null {
    const [expanded, setExpanded] = useState(false);
    if (!validation.result) return null;

    const { valid, message, checks = [], sampleRecord } = validation.result;
    const hasDetails = checks.length > 0 || !!sampleRecord;

    if (valid) {
        return (
            <Stack gap={6}>
                <Alert variant="light" color="green" icon={<CheckCircle2 size={16} />} p="xs">
                    <Group justify="space-between" gap="xs" wrap="nowrap">
                        <Text size="xs">Connection is valid</Text>
                        {hasDetails && (
                            <Anchor component="button" type="button" size="xs" onClick={() => setExpanded((v) => !v)}>
                                {expanded ? 'Hide details' : 'Show details'}
                            </Anchor>
                        )}
                    </Group>
                </Alert>
                {hasDetails && (
                    <Collapse in={expanded}>
                        <Stack gap={6}>
                            {checks.length > 0 && (
                                <Stack gap={4}>
                                    {checks.map((c) => (
                                        <CheckRow key={c.name} check={c} />
                                    ))}
                                </Stack>
                            )}
                            {sampleRecord && <SampleRecordAlert sampleRecord={sampleRecord} />}
                        </Stack>
                    </Collapse>
                )}
            </Stack>
        );
    }

    return (
        <Stack gap={6}>
            {checks.length === 0 ? (
                <Alert variant="light" color="red" icon={<XCircle size={16} />} p="xs">
                    <Text size="xs">{message ?? 'Connection is invalid'}</Text>
                </Alert>
            ) : (
                <Stack gap={4}>
                    {checks.map((c) => (
                        <CheckRow key={c.name} check={c} />
                    ))}
                </Stack>
            )}
            {sampleRecord && <SampleRecordAlert sampleRecord={sampleRecord} />}
        </Stack>
    );
}

function SampleRecordAlert({ sampleRecord }: { sampleRecord: string }): React.ReactElement {
    return (
        <Alert variant="light" color="blue" icon={<FileText size={16} />} title="Latest AI usage record" p="xs" mt={4}>
            <Code block style={{ fontSize: 12, lineHeight: 1.45, marginTop: 4 }}>
                {sampleRecord}
            </Code>
        </Alert>
    );
}

function CheckRow({ check }: { check: ConnectionTestCheck }): React.ReactElement {
    const color = check.status === 'pass' ? 'green' : check.status === 'warn' ? 'yellow' : 'red';
    const Icon = check.status === 'pass' ? CheckCircle2 : check.status === 'warn' ? AlertTriangle : XCircle;
    return (
        <Group gap={6} align="flex-start" wrap="nowrap">
            <Icon size={14} color={`var(--mantine-color-${color}-6)`} style={{ marginTop: 2, flexShrink: 0 }} />
            <Stack gap={0} style={{ flex: 1 }}>
                <Text size="xs" fw={500}>
                    {check.name}
                </Text>
                {check.message && (
                    <Text size="xs" c="dimmed">
                        {check.message}
                    </Text>
                )}
                {check.status !== 'pass' && check.remediation && (
                    <Text size="xs" c="dimmed" fs="italic">
                        {check.remediation}
                    </Text>
                )}
            </Stack>
        </Group>
    );
}
